import { useMemo } from 'react';
import type { ProjectDetail, Collector } from '@/types/odoo';
import { faseAggregate, roundSystemMix } from '@/utils/dashboardFases';
import { DashboardSysteemBreakdown } from './DashboardSysteemBreakdown';
import { m2BasisNotice, resolveM2Basis } from '@/utils/m2Basis';

interface DashboardHeaderCardProps {
  detail: ProjectDetail;
  collectors: Collector[] | null;
  collectorsLoading: boolean;
  collectorsError: string | null;
}

const fmtM2 = (n: number): string => `${Math.round(n)} m²`;

/**
 * Dashboard header — project identity + the m² trio (offerte / legplan /
 * uitgevoerd) + key info row. Fase 1 of the dashboard revamp.
 *
 * Data sources (all read-only):
 * - offerte m²    → saleOrder.x_studio_totaal_aantal_m2_offerte
 * - legplan m²    → project.x_studio_totaal_aantal_m2_legplan, falling back to the
 *                   OFFERTE m² when the stored field is 0 (>50% of projects have
 *                   legplan=0 — verified). It used to fall back to the sum of
 *                   collector m²; that is the same set of collectors a PL ticks
 *                   when vordering, so the percentage divided a number by itself
 *                   and read 100% (see utils/m2Basis).
 * - uitgevoerd m² → derived HERE from the collector list (Σ x_studio_collector_m2
 *                   where x_studio_collector_op_druk), NOT from Odoo's stored
 *                   x_uitgevoerd_totaal_aantal. ADR-028 decided that basis but the
 *                   Odoo formula was never repointed: it still sums manually
 *                   toggled planning tasks, and 122 of the 140 projects with a
 *                   nonzero stored value disagree with collector reality (see the
 *                   ADR-028 amendment in docs/decisions.md). Deriving it here also
 *                   makes the tile and the systeem breakdown beneath it the SAME
 *                   number by construction — one faseAggregate call feeds both.
 */
export function DashboardHeaderCard({ detail, collectors, collectorsLoading, collectorsError }: DashboardHeaderCardProps) {
  const { saleOrder, project } = detail;

  // Multi-SO badge: count the project's customer SOs. The "● N SO's" pill appears
  // next to the S-number only when there's more than one (single-SO projects are
  // unchanged). m² trio stays sourced from the base `saleOrder`.
  const soCount = detail.saleOrders?.length ?? 0;

  const sNummer = saleOrder?.name ?? '—';
  const klant = saleOrder?.partner_id ? saleOrder.partner_id[1] : '—';
  // Systeem from the offerte lines (server-derived "Thermoduct X-systeem"), shown
  // dominant-first with m² — visible before any collector exists. Falls back to the
  // VO-omschrijving Studio field (NB: intentionally "misspelled" to mirror Odoo),
  // then "—". The VO field is empty on most offertes, hence the line-derived source.
  const offerteSystemen = detail.offerteSystemen ?? [];
  // One label per system, each shown on its own line (see SysteemItem).
  const systeemLines = offerteSystemen.length > 0
    ? offerteSystemen.map((s) => (s.m2 > 0 ? `${s.systeem} · ${Math.round(s.m2)} m²` : s.systeem))
    : [saleOrder?.x_studio_vo_omschrijvring || '—'];
  const offerteM2 = saleOrder?.x_studio_totaal_aantal_m2_offerte ?? 0;
  // ONE aggregate over the project's whole collector list, feeding BOTH the
  // "Uitgevoerd m²" figure and the systeem breakdown printed under it. They are
  // the same computation, not two that happen to agree: agg.uitgevoerdM2 is by
  // construction Σ agg.systemMix[].uitgevoerdM2 (see utils/dashboardFases).
  const agg = useMemo(() => faseAggregate(collectors ?? []), [collectors]);
  // The collector fetch is async and separate from the detail response, so the
  // tile needs the same three states legplan has. PENDING = not resolved yet
  // (loading, or not started: still null with no error). UNAVAILABLE = resolved
  // to nothing we can trust — that must read as "onbekend" (—), never as a
  // confident 0 m². An empty-but-successful list is a genuine 0.
  //
  // UNAVAILABLE has TWO shapes, and the second is the dangerous one: the store
  // does NOT clear `collectors` when a fetch fails, so after a failed REFETCH of
  // the same project (stores/projectView.fetchCollectoren) the list is still the
  // pre-mutation snapshot. Several places refetch right after a write — the
  // op-druk toggle, collector create/edit, photo upload, vordering. Toggle a
  // collector op druk on a project where nothing was, lose the refetch, and a
  // `collectors !== null` check would happily re-print a confident "0 m² · 0%".
  // So a set `collectorsError` means unavailable even with a list in hand, and
  // the breakdown below is handed THIS flag rather than deriving its own.
  const collectorsPending = collectorsLoading || (collectors === null && collectorsError === null);
  const collectorsUnavailable = !collectorsPending && (collectors === null || collectorsError !== null);
  const uitgevoerdM2 = agg.uitgevoerdM2;

  // Legplan: stored field if set, else the offerte m² — the surface actually sold.
  // NEVER the collector sum (see utils/m2Basis).
  const legplanStored = project?.x_studio_totaal_aantal_m2_legplan ?? 0;
  const m2Basis = resolveM2Basis(legplanStored, offerteM2);
  const needsFallback = m2Basis.source !== 'legplan';
  const legplanM2 = m2Basis.value;
  // Both m² sources arrive with the detail response, so there is no async state
  // left to model here: a project with neither a stored legplan nor an offerte m²
  // is "unknown" (—), never a confident 0 m² and never a percentage.
  const legplanUnavailable = m2Basis.source === 'none';
  const m2Notice = m2BasisNotice(m2Basis);

  // Systeem breakdown under the "Uitgevoerd m²" tile. Independent of the fase
  // tabs — it comes from the project's whole collector list, so it renders on
  // single-fase projects too (which never get an "Alles" tab). Same `agg` as the
  // tile above, and rounded HERE against the very number the tile prints, so the
  // printed lines add up to the printed headline even on fractional m² (61.5 +
  // 111.5 would otherwise print "62" + "112" under a "173" tile).
  const systeemLijnen = useMemo(
    () => roundSystemMix(agg.systemMix, agg.uitgevoerdM2, agg.totalM2),
    [agg],
  );

  // Round both sides before diffing so a sub-integer difference never renders "+0".
  // Only meaningful against a real stored legplan — on the offerte fallback the two
  // numbers ARE the same number.
  const offerteDelta = !needsFallback && offerteM2 > 0 && legplanM2 > 0
    ? Math.round(legplanM2) - Math.round(offerteM2)
    : 0;
  const offertePct = offerteDelta !== 0 ? Math.round((offerteDelta / offerteM2) * 100) : 0;
  const pct = !collectorsUnavailable && !collectorsPending && legplanM2 > 0
    ? Math.min(100, Math.round((uitgevoerdM2 / legplanM2) * 100))
    : null;

  return (
    <section
      aria-labelledby="dashboard-heading"
      className="rounded-2xl border border-gray-200 border-t-4 border-t-td-blue bg-white p-5 shadow-sm"
    >
      {/* Identity (no "in opbouw" badge on the data card — it would read as
          "these numbers are unreliable"; the WIP signal lives in DashboardTab). */}
      <div className="min-w-0">
        <h2 id="dashboard-heading" className="text-lg font-extrabold text-td-navy leading-tight">
          {detail.projectName}
        </h2>
        <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-td-muted">
          <span>{sNummer}</span>
          {soCount > 1 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-td-blue/30 bg-td-blue/10 px-2 py-0.5 text-[11px] font-bold text-td-blue">
              <span aria-hidden="true">●</span>
              {soCount} SO&apos;s
            </span>
          )}
        </p>
        <p className="mt-1 text-sm font-semibold text-td-navy">{klant}</p>
      </div>

      <hr className="my-4 border-gray-200" />

      {/* m² trio */}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile label="Offerte m²" hint="Verkocht aan klant">
          <span className="text-xl font-extrabold text-td-navy">{fmtM2(offerteM2)}</span>
        </KpiTile>

        <KpiTile
          label="Legplan m²"
          hint={
            m2Basis.source === 'legplan'
              ? 'Berekend uit tekening'
              : m2Basis.source === 'offerte'
                ? 'Niet ingevuld — offerte m²'
                : 'Niet ingevuld'
          }
        >
          <span>
            {legplanUnavailable ? (
              <span className="text-xl font-extrabold text-td-light" aria-label="Legplan niet beschikbaar">
                —
              </span>
            ) : (
              <span className={`text-xl font-extrabold ${m2Basis.deviates ? 'text-red-600' : 'text-td-navy'}`}>
                {fmtM2(legplanM2)}
                {offerteDelta !== 0 && (
                  <span
                    className={`ml-1.5 text-xs font-bold ${
                      m2Basis.deviates ? 'text-red-600' : offerteDelta > 0 ? 'text-td-magenta' : 'text-td-green'
                    }`}
                  >
                    {`${offerteDelta > 0 ? '+' : ''}${offerteDelta} (${offertePct > 0 ? '+' : ''}${offertePct}%)`}
                  </span>
                )}
              </span>
            )}
          </span>
          {/* The number every percentage on this project divides by — say so out
              loud when it is not the legplan, or when the legplan is far off the
              surface that was actually sold. */}
          {m2Notice && (
            <p
              role="alert"
              className={`mt-1.5 text-[11px] font-semibold ${m2Notice.tone === 'danger' ? 'text-red-600' : 'text-amber-700'}`}
            >
              ⚠ {m2Notice.text}
            </p>
          )}
        </KpiTile>

        <KpiTile label="Uitgevoerd m²" hint="Op druk gezette collectoren">
          {/* aria-live on the STABLE wrapper, as on legplan, so the async
              …→value / …→— transition is announced. */}
          <div className="flex items-baseline gap-1.5" aria-live="polite" aria-atomic="true">
            {collectorsPending ? (
              <span className="text-xl font-extrabold text-td-light" aria-label="Uitgevoerd laden">
                …
              </span>
            ) : collectorsUnavailable ? (
              <span
                className="text-xl font-extrabold text-td-light"
                aria-label={collectorsError ? `Uitgevoerd niet beschikbaar: ${collectorsError}` : 'Uitgevoerd niet beschikbaar'}
                title={collectorsError ?? undefined}
              >
                —
              </span>
            ) : (
              <span className="text-xl font-extrabold text-td-navy">{fmtM2(uitgevoerdM2)}</span>
            )}
            {pct !== null && <span className="text-xs font-bold text-td-muted">{`/ ${Math.round(legplanM2)}`}</span>}
            {pct !== null && <span className="ml-auto text-xs font-bold text-td-green">{`${pct}%`}</span>}
          </div>
          {pct !== null && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full bg-td-green" style={{ width: `${pct}%` }} />
            </div>
          )}
          {/* Which systemen make up that number — printed, not on hover. */}
          <DashboardSysteemBreakdown
            lines={systeemLijnen}
            pending={collectorsPending}
            unavailable={collectorsUnavailable}
            error={collectorsError}
          />
        </KpiTile>
      </div>

      {/* Info row */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <SysteemItem lines={systeemLines} />
        <InfoItem label="Projectleider" value={detail.projectleiderName || '—'} />
        <WerflocatieItem contact={detail.werfContact} fallback={detail.werfLocation} />
      </div>
    </section>
  );
}

function KpiTile({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">{label}</p>
      <div className="mt-1">{children}</div>
      <p className="mt-1 text-[11px] text-td-light">{hint}</p>
    </div>
  );
}

/**
 * Tokens in a contact NAME that mean "this is clearly NOT a building site" — a
 * warehouse, the company HQ, or a placeholder partner. Matched case-insensitively
 * against the name only (not the address, to avoid flagging real streets like
 * "Magazijnstraat").
 */
const NOT_A_WERF = [
  'magazijn',
  'warehouse',
  'depot',
  'hoofdkantoor',
  'hoofdzetel',
  'thermoduct',
  'onbekend',
  'unknown',
  'n.v.t',
  'nvt',
];

/**
 * Placeholder phrases that mean "the street/address is missing". Matched against
 * the ADDRESS (not just the name) — these are full phrases no real street is
 * named, so they won't false-positive on a genuine street like "Magazijnstraat".
 */
const PLACEHOLDER_ADDRESS = [
  'niet gekend',
  'onbekend',
  'unknown',
  'n.v.t',
  'nvt',
  'geen adres',
];

/**
 * Is this werf contact clearly NOT a building site? True when the name matches a
 * NOT_A_WERF token / is empty, or the address is a placeholder. We deliberately
 * do NOT use the Odoo partner `type` ('contact' vs 'delivery'): in our data
 * legit werf addresses are a ~50/50 split between the two, so type is pure noise.
 */
function isBadWerf(contact: { name: string; address: string }): boolean {
  const name = contact.name.trim().toLowerCase();
  const address = contact.address.toLowerCase();
  const nameBad = name === '' || NOT_A_WERF.some((t) => name.includes(t));
  const addrBad = PLACEHOLDER_ADDRESS.some((t) => address.includes(t));
  return nameBad || addrBad;
}

/**
 * Werflocatie = the werf delivery contact (SO partner_shipping_id). Shows the
 * contact NAME + full address so a wrong contact (a magazijn or the company HQ
 * instead of the site) is spotted at a glance. A RED flag warns when the contact
 * is clearly not a building site (magazijn, HQ, onbekend name, placeholder
 * address — see isBadWerf). Falls back to the legacy zip-city string on older
 * responses without werfContact.
 */
function WerflocatieItem({
  contact,
  fallback,
}: {
  contact?: { name: string; type: string; address: string; isDelivery: boolean } | null;
  fallback: string | null;
}) {
  if (!contact) {
    return <InfoItem label="Werflocatie" value={fallback || '—'} />;
  }
  const isBad = isBadWerf(contact);
  return (
    <div className={`min-w-0 ${isBad ? 'rounded-lg border border-red-300 bg-red-50 px-2 py-1' : ''}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Werflocatie</p>
      <p className={`mt-0.5 flex items-center gap-1 text-sm font-bold ${isBad ? 'text-red-700' : 'text-td-navy'}`}>
        <span className="truncate" title={contact.name}>{contact.name || '—'}</span>
        {isBad && (
          <span
            className="shrink-0 rounded bg-red-200 px-1 text-[10px] font-bold text-red-800"
            title={`'${contact.name || 'onbekend'}' lijkt geen werfadres — controleer of dit niet een magazijn, kantoor of hoofdzetel is`}
          >
            ⚠ geen werfadres
          </span>
        )}
      </p>
      {contact.address && (
        <p className={`mt-0.5 truncate text-xs ${isBad ? 'text-red-600' : 'text-td-muted'}`} title={contact.address}>{contact.address}</p>
      )}
    </div>
  );
}

/**
 * Systeem (offerte) — each system on its own line so multi-system offertes read
 * as a stacked list instead of a comma-run.
 */
function SysteemItem({ lines }: { lines: string[] }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Systeem (offerte)</p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {lines.map((line, i) => (
          <span
            key={i}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-td-blue/10 px-2.5 py-0.5 text-xs font-bold text-td-blue"
            title={line}
          >
            <span className="truncate">{line}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">{label}</p>
      <p className="mt-0.5 truncate text-sm font-bold text-td-navy" title={value}>
        {value}
      </p>
    </div>
  );
}
